import { createPortal } from 'react-dom';
import type { Team } from '../lib/api';
import { ratingText } from '../lib/rating/format';
import { squadList, type ListedTeam } from '../lib/squad-list';

/**
 * The league's squad list, as a document that can be printed or saved as a PDF.
 *
 * WHY THIS EXISTS AT ALL. The league circulated a squad list as a hand-made
 * PDF, and the database drifted from it — four seeds wrong and two squad places
 * wrong, found by comparing all 109 players rather than by anybody noticing.
 * The list was right and the database was wrong, which is the more dangerous
 * way round: the list is what captains read, the database is what the app
 * scores from, and nothing forced them to agree.
 *
 * This is built from the season the app is running, so it cannot disagree with
 * the site. Nothing here is typed in. The date it carries is the date it was
 * generated, which is what stops a stale copy circulating as though it were
 * current.
 *
 * PORTALLED TO THE BODY so the print stylesheet can hide the app around it.
 * Rendered inside the page it would sit under `.wrap`, and hiding `.wrap` for
 * print would take the document with it.
 */
export default function SquadListDoc({
  teams,
  leagueName,
  seasonName,
  cap,
  tiered = false,
  onClose,
}: {
  teams: Team[];
  leagueName: string;
  seasonName: string;
  /** The combined squad maximum, or null for a league without one. */
  cap: number | null;
  /** Whether the league grades players into tiers rather than rating them. */
  tiered?: boolean;
  onClose: () => void;
}) {
  const doc = squadList(teams, cap, tiered);

  /*
   * The day it was generated, in the league's own way of writing a date.
   *
   * Their list was headed "6Sep26" and this reads "9 September 2026" — longer,
   * and unambiguous to a reader who does not already know the convention. A
   * squad list is checked against a captain's memory of what was agreed, so the
   * date is doing real work rather than decorating the header.
   */
  const generated = new Date().toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return createPortal(
    <div className="squad-doc" role="dialog" aria-modal="true" aria-label="Squad list">
      <div className="squad-doc-bar">
        <button type="button" className="btn ghost" onClick={onClose}>
          ← Back
        </button>
        <button type="button" className="btn" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <article className="squad-page">
        <header className="squad-head">
          <h1>{leagueName}</h1>
          <p className="squad-sub">
            {seasonName} · Squad list
          </p>
          <p className="squad-meta">
            {doc.teamCount} teams · {doc.playerCount} players · as at {generated}
          </p>
        </header>

        <div className="squad-cols">
          {doc.teams.map((team) => (
            <TeamBlock key={team.id} team={team} cap={doc.cap} tiered={doc.tiered} />
          ))}
        </div>

        <footer className="squad-foot">
          <p>
            {doc.tiered
              ? 'Players are shown in the squad tier the league graded them into.'
              : `Ratings are the seeds the pair cap is judged on, as held by the league${
                  doc.cap === null ? '' : `. A squad may total no more than ${ratingText(doc.cap)}`
                }.`}
          </p>
          {/*
            The generated date again at the foot, because a printed page is read
            apart from the screen it came from and the header may be a page away
            by the time somebody is looking at the last team.
          */}
          <p>
            {leagueName} · {seasonName} · generated {generated}
          </p>
        </footer>
      </article>
    </div>,
    document.body,
  );
}

function TeamBlock({
  team,
  cap,
  tiered,
}: {
  team: ListedTeam;
  cap: number | null;
  tiered: boolean;
}) {
  /*
   * The headroom is only stated when it is knowable. A squad with an unrated
   * player has an incomplete total, and printing "0.03 left" against it would
   * hand a captain a number to act on that the league has not actually decided.
   */
  const known = team.unrated === 0;

  return (
    <section className="squad-team">
      <h2>
        <span className="squad-team-name">{team.name}</span>
        <span className="squad-team-count">
          {team.players.length} {team.players.length === 1 ? 'player' : 'players'}
        </span>
      </h2>

      <ol className="squad-players">
        {team.players.map((p) => (
          <li key={p.number}>
            <span className="squad-num">{p.number}</span>
            <span className="squad-name">
              {p.name}
              {p.substitute && <span className="squad-sub-tag">sub</span>}
            </span>
            <span className="squad-rating">
              {tiered ? (p.tier ?? 'Ungraded') : p.rating === null ? '–' : ratingText(p.rating)}
            </span>
          </li>
        ))}
      </ol>

      {/*
        A tiered league has no squad total, so it is given none. Not a zero and
        not an "incomplete" note — there is no number here that the league has
        decided, and inventing a line for it would say otherwise.
      */}
      {tiered ? null : (
      <p className={`squad-total${cap !== null && known && (team.headroom as number) < 0 ? ' squad-over' : ''}`}>
        <span>Squad total</span>
        <b>{ratingText(team.total)}</b>
        {cap !== null && known && (
          <span className="squad-headroom">
            {/*
              Three cases, not two. Bed Shop Dream Team sits at exactly 24.00
              and the two-case version printed "0.00 under 24.00" against them —
              literally true and the wrong thing to say, because it reads as
              room. They have none: the next signing has to be balanced by a
              release, which is precisely what a captain needs the line for.
            */}
            {(team.headroom as number) < 0
              ? `${ratingText(Math.abs(team.headroom as number))} over ${ratingText(cap)}`
              : (team.headroom as number) === 0
                ? `exactly on ${ratingText(cap)}`
                : `${ratingText(team.headroom as number)} under ${ratingText(cap)}`}
          </span>
        )}
        {!known && (
          <span className="squad-headroom">
            {team.unrated} unrated, so the total is incomplete
          </span>
        )}
      </p>
      )}
    </section>
  );
}
